<template>
  <form-block :title="title !== null ? title : tr('Conversion')">
    <template v-if="fbOdaxConversionEventIsVisible && !entity.disconnectedAdAccount" #top-right>
      <refresh-button
        :label="tr('Update events')"
        :success-message="tr('Conversion events updated successfully.')"
        :promise="() => refreshSelectedAdAccount()"
      />
    </template>

    <info-text v-if="entity.disconnectedAdAccount" type="error">
      {{ tr('The selected ad account has been disconnected.') }}
    </info-text>
    <template v-else>
      <template v-if="!minimal">
        <div v-if="destiantionTypeSelectVisible" class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Conversion location') }}</ui-label>
          <select-field
            v-model="entity.settings.adset_conversion_location"
            :options="destinationTypes"
            :selectable="item => !item.disabled"
            :reduce="(item) => item.machine_name"
            :aria-label="tr('Conversion location')"
            :disabled="entity.isRoasGoalCampaignStrategy || isEditMode"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Performance goal') }}</ui-label>
          <select-field
            v-model="entity.settings.adset_optimization_goal"
            :options="optimizationGoals"
            :reduce="(obj) => obj.machine_name"
            :aria-label="tr('Performance goal')"
            :disabled="isEditMode"
          />
        </div>
      </template>

      <template v-if="fbOdaxConversionAppIsVisible">
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Application') }}</ui-label>
          <select-field
            v-model="entity.settings.adset_conversion_app_id"
            :options="entity.appList"
            :reduce="(obj) => obj.id"
            :placeholder="tr('Select an app')"
            :disabled="isEditMode || disabled"
            :loading="isValidatingAppUrl"
            label="name"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Application type') }}</ui-label>
          <select-field
            v-model="entity.settings.adset_conversion_app_store_url"
            :options="appTypeList"
            :reduce="(obj) => obj.value"
            :placeholder="tr('Select app type')"
            :disabled="!entity.settings.adset_conversion_app_id || isEditMode || disabled"
            :loading="isValidatingAppUrl"
          />
          <info-text v-if="!isAppUrlValid && !isValidatingAppUrl" type="error">
            {{ tr('Invalid app store URL. Please ask the app developer to update the correct :store link in the app settings on the Facebook Developer platform.', {':store': storeName}) }}
          </info-text>
        </div>
      </template>

      <div v-if="fbOdaxConversionPixelIsVisible" class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Pixel') }}</ui-label>
        <select-field
          v-model="entity.settings.adset_conversion_pixel_id"
          :options="entity.pixelListForSelect"
          :reduce="option => option.value"
          :label="(option) => option.label"
          :placeholder="tr('Select a pixel')"
          :disabled="isEditMode || disabled"
        >
          <template #option="{ option }">
            <span
              v-if="'status' in option"
              :class="[ 'size-2 shrink-0 rounded-full', (option.status || $root.currentCompany.is_demo) ? 'bg-success' : 'bg-destructive' ]"
            />
            {{ option.label }}
          </template>
        </select-field>
      </div>

      <template v-if="fbOdaxConversionEventIsVisible">
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Conversion event') }}</ui-label>
          <select-field
            v-model="entity.settings.adset_conversion_event"
            :options="conversionEvents"
            :reduce="(obj) => obj.machine_name"
            :placeholder="tr('Select a conversion')"
            :disabled="entity.isRoasGoalCampaignStrategy || isEditMode || disabled"
            :selectable="option => !option.disabled"
          >
            <template #option="{ option }">
              <span
                v-tooltip.left="option.disabled ? tr('This conversion is not available for the selected objective.') : null"
                class="flex min-w-0 items-center gap-2"
              >
                <span
                  v-if="'status' in option"
                  :class="[ 'size-2 shrink-0 rounded-full', (option.status || $root.currentCompany.is_demo) ? 'bg-success' : 'bg-destructive' ]"
                />
                {{ tr(option.label) }}
              </span>
            </template>
          </select-field>
        </div>

        <a
          v-if="!minimal"
          href="https://business.facebook.com/settings/custom-conversions"
          class="w-fit text-sm text-primary underline"
          target="_blank"
        >
          {{ tr('Create new custom conversion') }}
        </a>
      </template>

      <template v-if="!minimal">
        <div v-if="entity.isRoasGoalAvailable" class="flex flex-col gap-1.5">
          <ui-label>
            {{ tr('ROAS goal') }}
            <template v-if="!entity.isRoasGoalCampaignStrategy">
              ({{ tr('optional') }})
            </template>
          </ui-label>
          <p class="m-0 text-sm text-muted-foreground">{{ tr('Set a goal if you’re aiming for a certain return on ad spend. To set your ROAS goal, use a number between 0.001 and 1,000.') }}</p>
          <number-input
            v-model="entity.settings.adset_roas_goal"
            class="max-w-40"
            :step="0.001"
            :max="1000"
            :disabled="isEditMode || disabled"
          />
        </div>
        <div v-else-if="entity.bidAmountIsAvailable" class="flex flex-col gap-1.5">
          <ui-label>
            {{ bidAmountLabel }}
            <template v-if="!entity.bidAmountIsRequired">
              ({{ tr('optional') }})
            </template>
          </ui-label>
          <p v-if="!entity.isBidCapStrategy" class="m-0 text-sm text-muted-foreground">{{ tr('A type of advertising metric that is used to measure the effectiveness and efficiency of an advertising campaign. It represents the average cost that you pay for a specific result, such as a click, an impression, a lead, or a sale.') }}</p>
          <money-input
            v-model="entity.settings.adset_bid_amount"
            class="max-w-60"
            :currency="entity.adAccountCurrency ? entity.adAccountCurrency.code : 'EUR'"
            :disabled="isEditMode || disabled"
          />
          <info-text v-if="!entity.bidAmountIsRequired">
            {{ tr('Meta will aim to spend your entire budget and get the most link clicks using the highest volume bid strategy. If keeping the average cost per result around a certain amount is important, enter a cost per result goal.') }}
          </info-text>
        </div>
      </template>
    </template>
  </form-block>
</template>

<script>
import { CTA_URL_USAGE } from '@/consts.js';
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import ConversionBlockMixin from '@/mixins/RuleForm/Blocks/ConversionBlock.js';
import MetaOdaxMixin from '@/mixins/RuleForm/Functions/MetaOdax.js';
import AppUrlValidatorService from '@/services/AppUrlValidatorService.js';
import InfoText from '@/ui/components/InfoText.vue';
import MoneyInput from '@/ui/components/MoneyInput.vue';
import NumberInput from '@/ui/components/NumberInput.vue';
import RefreshButton from '@/ui/components/RefreshButton.vue';
import SelectField from '@/ui/components/SelectField.vue';
import { pickBestFuzzyMatch } from '@/utils/fuzzyMatch.js';

export default {
  name: 'ConversionOdaxBlock',
  components: {
    InfoText,
    MoneyInput,
    NumberInput,
    RefreshButton,
    SelectField,
  },
  mixins: [ RuleFormBlockMixin, ConversionBlockMixin, MetaOdaxMixin ],
  props: {
    title: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    minimal: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      isValidatingAppUrl: false,
      isAppUrlValid: false,
    };
  },
  computed: {
    // All conversion settings are disabled in edit mode to ensure reporting consistency
    isEditMode() {
      return !this.entity.isNew;
    },
    selectedObjective() {
      const { objective } = this.entity.output_campaign;
      return this.fbOdaxMap.find(item => item.machine_name === objective);
    },
    destinationTypes() {
      const list = JSON.parse(JSON.stringify(this.selectedObjective.destination_types));
      list.forEach(item => {
        if (item.machine_name === 'instagram_profile') {
          item.disabled = this.entity.hasSelectedFacebookPlatform || !this.entity.hasSelectedInstagramPlatform;
        }
      });

      return list;
    },
    selectedDestinationType() {
      if (this.entity.settings.adset_conversion_location === null) {
        return null;
      }

      return this.destinationTypes.find(item => item.machine_name === this.entity.settings.adset_conversion_location);
    },
    optimizationGoals() {
      if (!this.selectedDestinationType) {
        return [];
      }

      return this.conditionalFilteredList(this.selectedDestinationType.optimization_goals);
    },
    destiantionTypeSelectVisible() {
      return this.destinationTypes.length > 1;
    },
    appTypeList() {
      if (!this.entity.selectedApp) {
        return [];
      }

      const list = [];
      if (this.entity.selectedApp.hasGooglePlayApp) {
        list.push({ value: this.entity.selectedApp.google_play_url, label: 'Google Play' });
      }
      if (this.entity.selectedApp.getAppStoreApp) {
        list.push({ value: this.entity.selectedApp.app_store_url, label: 'Apple App Store' });
      }

      return list;
    },
    bidAmountLabel() {
      return this.entity.isBidCapStrategy ? this.tr('Bid cap') : this.tr('Cost per result goal');
    },
    storeName() {
      if (this.appTypeList.length === 0) {
        return '';
      }
      return this.appTypeList.find(i => i.value === this.entity.settings.adset_conversion_app_store_url).label;
    },
    fuzzyMatchReferences() {
      const refs = [];
      const brand = this.entity.selectedBrand;
      if (brand?.name) {
        refs.push(brand.name);
      }
      if (Array.isArray(this.entity.source_profiles)) {
        for (const sp of this.entity.source_profiles) {
          if (sp?.name) {
            refs.push(sp.name);
          }
        }
      }
      return refs;
    },
  },
  watch: {
    'entity.ad_account.id'(val) {
      if (!val) {
        return;
      }

      this.updateConversionEvents();
    },
    'entity.isAppPromo'(val) {
      if (val) {
        this.entity.settings.adset_conversion_location = 'app';
      }
      else {
        this.entity.settings.adset_conversion_location = 'website';
      }

      this.$nextTick(() => this.updateConversionEvents());
    },
    'entity.settings.adset_conversion_location'(val, oldVal) {
      if (this.optimizationGoals.length === 0) {
        this.entity.settings.adset_optimization_goal = null;
      }
      else {
        this.entity.settings.adset_optimization_goal = this.optimizationGoals[0].machine_name;
      }

      if (val === 'instagram_profile') {
        this.entity.settings.cta_button = 'NO_BUTTON';
      }

      const ctaUrlUsageOf = conversionLocation => this.$store.getters['app/facebookOdaxCtaUrlUsage']({
        campaignObjective: this.entity.output_campaign?.objective,
        conversionLocation,
      });
      if (ctaUrlUsageOf(val) === CTA_URL_USAGE.NOT_USED || (oldVal && ctaUrlUsageOf(oldVal) === CTA_URL_USAGE.APP_STORE)) {
        this.entity.settings.cta_url = '';
      }

      if (val !== 'website') {
        this.entity.settings.adset_conversion_pixel_id = null;
        this.entity.settings.adset_conversion_event = null;
      }

      if (val !== 'app') {
        this.entity.settings.adset_conversion_app_id = null;
        this.entity.settings.adset_conversion_app_store_url = null;
        this.entity.settings.tracking_advertisable_application_id = null;
      }
    },
    'entity.settings.adset_optimization_goal'(val) {
      const isLeadGoal = [ 'LEAD_GENERATION', 'QUALITY_LEAD' ].includes(val);
      if (!isLeadGoal) {
        this.entity.settings.leadgen_form_id = null;
      }
    },
    fbOdaxConversionAppIsVisible(val) {
      if (!val) {
        this.entity.settings.adset_conversion_app_id = null;
        this.entity.settings.adset_conversion_app_store_url = null;
      }
    },
    fbOdaxConversionPixelIsVisible(val) {
      if (!val) {
        this.entity.settings.adset_conversion_pixel_id = null;
      }
    },
    fbOdaxConversionEventIsVisible(val) {
      if (!val) {
        this.entity.settings.adset_conversion_event = null;
      }
    },
    appTypeList(val) {
      if (val.length !== 0) {
        this.entity.settings.adset_conversion_app_store_url = val[0].value;
      }
    },
    'entity.pixelListForSelect'(val) {
      if (val.length !== 0) {
        this.entity.settings.adset_conversion_pixel_id = val[0].value;
      }
    },
    'entity.settings.adset_conversion_app_id'(val) {
      if (this.entity.isAppPromo) {
        this.entity.settings.tracking_advertisable_application_id = val;
      }
    },
    'entity.settings.adset_conversion_app_store_url': {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.validateAppUrl();
        });
      },
    },
  },
  mounted() {
    if (
      !this.entity.settings.adset_conversion_location
      || !this.destinationTypes.map(i => i.machine_name).includes(this.entity.settings.adset_conversion_location)
      || this.destinationTypes.find(i => i.machine_name === this.entity.settings.adset_conversion_location).disabled
    ) {
      this.entity.settings.adset_conversion_location = this.destinationTypes.filter(i => !i.disabled)[0].machine_name;
    }
    else if (!this.optimizationGoals.map(i => i.machine_name).includes(this.entity.settings.adset_optimization_goal)) {
      this.entity.settings.adset_optimization_goal = this.optimizationGoals[0].machine_name;
    }

    if (
      (!this.entity.settings.adset_conversion_pixel_id && !this.entity.settings.adset_conversion_app_id)
      || (
        this.entity.settings.adset_conversion_pixel_id
        && !this.entity.pixelListForSelect.map(i => i.value).includes(this.entity.settings.adset_conversion_pixel_id)
      )
      || (
        this.entity.settings.adset_conversion_app_id
        && !this.entity.appList.map(i => i.id).includes(this.entity.settings.adset_conversion_app_id)
      )
    ) {
      this.updateConversionEvents();
    }
  },
  methods: {
    updateConversionEvents() {
      this.$nextTick(() => {
        if (this.entity.isAppPromo) {
          if (this.entity.appList.length === 0) {
            this.entity.settings.adset_conversion_app_id = null;
          }
          else if (this.entity.appList.length === 1 && this.entity.settings.adset_conversion_app_id !== this.entity.appList[0].id) {
            this.entity.settings.adset_conversion_app_id = this.entity.appList[0].id;
          }
          else if (!this.entity.appList.map(i => i.id).includes(this.entity.settings.adset_conversion_app_id)) {
            const best = pickBestFuzzyMatch(this.fuzzyMatchReferences, this.entity.appList, app => app.name);
            this.entity.settings.adset_conversion_app_id = best ? best.id : null;
          }

          if (this.appTypeList.length === 0) {
            this.entity.settings.adset_conversion_app_store_url = null;
          }
          else if (this.appTypeList.length === 1 && this.entity.settings.adset_conversion_app_store_url !== this.appTypeList[0].value) {
            this.entity.settings.adset_conversion_app_store_url = this.appTypeList[0].value;
          }
          else if (!this.appTypeList.map(i => i.value).includes(this.entity.settings.adset_conversion_app_store_url)) {
            this.entity.settings.adset_conversion_app_store_url = null;
          }

          this.entity.settings.adset_conversion_pixel_id = null;
          this.entity.settings.adset_conversion_event = null;
        }
        else {
          if (this.entity.pixelListForSelect.length === 0 || !this.fbOdaxConversionPixelIsVisible) {
            this.entity.settings.adset_conversion_pixel_id = null;
          }
          else if (this.entity.pixelListForSelect.length === 1 && this.entity.settings.adset_conversion_pixel_id !== this.entity.pixelListForSelect[0].value) {
            this.entity.settings.adset_conversion_pixel_id = this.entity.pixelListForSelect[0].value;
          }
          else if (!this.entity.pixelListForSelect.map(i => i.value).includes(this.entity.settings.adset_conversion_pixel_id)) {
            const best = pickBestFuzzyMatch(this.fuzzyMatchReferences, this.entity.pixelList, p => p.name);
            this.entity.settings.adset_conversion_pixel_id = best ? best.id : null;
          }

          if (this.conversionEvents.length === 0 || !this.fbOdaxConversionEventIsVisible) {
            this.entity.settings.adset_conversion_event = null;
          }
          else if (this.entity.isSalesCampaign) {
            this.entity.settings.adset_conversion_event = 'PURCHASE';
          }
          else if (!this.conversionEvents.map(i => i.machine_name).includes(this.entity.settings.adset_conversion_event)) {
            this.entity.settings.adset_conversion_event = this.conversionEvents[0].machine_name;
          }

          this.entity.settings.adset_conversion_app_id = null;
          this.entity.settings.adset_conversion_app_store_url = null;
        }
      });
    },
    validate() {
      if (this.isValidatingAppUrl) {
        this.infoToast(this.tr('Please wait until the application\'s CTA url is validated.'));
        return false;
      }

      if (this.fbOdaxConversionPixelIsVisible && !this.entity.settings.adset_conversion_pixel_id) {
        this.error(this.tr('When a website is used as a conversion location, Pixel must be added!'));
        return false;
      }
      if (this.fbOdaxConversionEventIsVisible && !this.entity.settings.adset_conversion_event) {
        this.error(this.tr('Conversion event must be added!'));
        return false;
      }
      if (this.fbOdaxConversionAppIsVisible) {
        if (!this.entity.settings.adset_conversion_app_id) {
          this.error(this.tr('When an application is used as a conversion location, App must be added!'));
          return false;
        }
        if (!this.entity.settings.adset_conversion_app_store_url) {
          this.error(this.tr('When an application is used as a conversion location, Application type must be added!'));
          return false;
        }
        if (!this.isAppUrlValid) {
          this.error(this.tr('The application\'s CTA url is not valid.'));
          return false;
        }
      }

      if (this.entity.bidAmountIsRequired && this.entity.settings.adset_bid_amount <= 0) {
        if (this.entity.isBidCapStrategy) {
          this.error(this.tr(':field value is required if you use "Bid Cap" bid strategy!', { ':field': this.bidAmountLabel }));
          return false;
        }
        if (this.entity.isCostPerResultGoalStrategy) {
          this.error(this.tr(':field value is required if you use "Cost per result goal" bid strategy!', { ':field': this.bidAmountLabel }));
          return false;
        }
      }

      if (this.entity.isRoasGoalAvailable) {
        if (this.entity.isRoasGoalCampaignStrategy && this.entity.settings.adset_roas_goal <= 0) {
          this.error(this.tr(':field value is required if you use "ROAS goal" campaign bid strategy!', { ':field': this.tr('ROAS goal') }));
          return false;
        }

        if (
          this.entity.settings.adset_roas_goal !== null
          && this.entity.settings.adset_roas_goal.toString().length !== 0
          && this.entity.settings.adset_roas_goal <= 0
        ) {
          this.error(this.tr('validation.min', { '{field}': this.tr('ROAS goal') }));
          return false;
        }
        else if (this.entity.settings.adset_roas_goal > 1000) {
          this.error(this.tr('validation.max', { '{field}': this.tr('ROAS goal') }));
          return false;
        }
      }

      return true;
    },
    validateAppUrl() {
      this.isValidatingAppUrl = true;
      this.isAppUrlValid = true;
      this.$nextTick(() => {
        if (!this.entity.settings.adset_conversion_app_store_url) {
          this.isAppUrlValid = true;
          this.isValidatingAppUrl = false;
          return false;
        }

        AppUrlValidatorService.validateUrl(this.entity.settings.adset_conversion_app_store_url).then(appUrlValidatorResponse => {
          this.isValidatingAppUrl = false;
          if (appUrlValidatorResponse && appUrlValidatorResponse.is_valid) {
            this.isAppUrlValid = true;
            return true;
          }
          this.isAppUrlValid = false;
          return false;
        }).catch(() => {
          this.isAppUrlValid = false;
          this.isValidatingAppUrl = false;
        }).finally(() => {
          this.isValidatingAppUrl = false;
        });

        return this.isAppUrlValid;
      });
    },
  },
};
</script>
